<script setup lang="ts">
import DataTable from 'primevue/datatable'
import { computed, ref, onMounted } from 'vue'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import TableFilter from '@/components/table/TableFilter.vue'
import Button from 'primevue/button'
import { Icon } from '@iconify/vue/dist/iconify.js'
import Column from 'primevue/column'
import { formatedDate, generateUniqueId } from '@/constants/index'
import InputText from 'primevue/inputtext'
import Calendar from 'primevue/calendar'
import { addMergeDeliveryToMerge, getYarnById, updateMergeDelivery, deleteMergeDelivery } from '@/api/yarns'
import { useRouter } from 'vue-router'
import { getSupplierById } from '@/api/suppliers'
import type { Delivery } from '@/types/yarn-types'
import { useMergeForm } from '@/composables/useMergeForm'
import Dialog from 'primevue/dialog'

const emit = defineEmits<{
  yarnUpdated: [yarn: any]
}>()

const router = useRouter()
const { loading, showToast } = useMergeForm('view')
const yarn = ref<any>()
const searchQuery = ref('')
const editingRow = ref(null)
const isEditingRow = (rowId: string) => editingRow.value === rowId
const deliveries = ref<Delivery[]>([])
const isAddingNew = ref<boolean>(false)
const supplierName = ref<string>('')

const showWarningModal = ref(false)
const warningMessage = ref('')
const pendingOperation = ref<{
  type: 'add' | 'update'
  id: string
  callback: () => void
} | null>(null)

const yarnId = router.currentRoute.value.params.id as string

const filteredLinkedProducts = computed(() => {
  const query = searchQuery.value.toLowerCase()
  return deliveries.value.filter(
    (deliveryKilo: Delivery) =>
      String(deliveryKilo.kiloOfYarnDelivered).includes(query) ||
      deliveryKilo.dateReceived?.includes(query)
  )
})

const getSupplierName = (supplierId: string | undefined) => {
  return supplierName.value || 'Unknown Supplier'
}
const addDelivery = () => {
  const id = generateUniqueId()

  const newRow = {
    id,
    kiloOfYarnDelivered: '',
    dateReceived: '',
    supplierId: yarn.value?.supplierId,
    isNew: true
  }

  deliveries.value.push(newRow)
  handleEnableEdit(id)
  isAddingNew.value = true
}

const handleEnableEdit = (id: any) => {
  editingRow.value = id
}

const handleSave = async (index: number, id: any) => {
  const item = deliveries.value.find((item) => item.id == id)
  editingRow.value = null
  isAddingNew.value = false

  const newDeliveryAmount = Number(item?.kiloOfYarnDelivered)

  const existingDeliveredAmount = deliveries.value
    .filter(delivery => delivery.id !== id)
    .reduce((total, delivery) => total + Number(delivery.kiloOfYarnDelivered), 0)

  const totalDeliveredAfterNew = existingDeliveredAmount + newDeliveryAmount

  if (yarn.value?.totalKgOrdered && totalDeliveredAfterNew > yarn.value.totalKgOrdered) {
    const message = `Warning: This delivery would result in a total delivered amount of ${totalDeliveredAfterNew} kg, which exceeds the total ordered amount of ${yarn.value.totalKgOrdered} kg. Do you want to continue?`

    showWarningModal.value = true
    warningMessage.value = message
    pendingOperation.value = {
      type: 'add',
      id,
      callback: () => performSave(id, item)
    }
    return
  }

  await performSave(id, item)
}

const performSave = async (id: string, item: any) => {
  const delivery = {
    kiloOfYarnDelivered: Number(item?.kiloOfYarnDelivered),
    dateReceived: item?.dateReceived,
    supplierId: yarn.value?.supplierId
  }

  await addMergeDeliveryToMerge(delivery, yarnId)
  .then(async () => {
    showToast('success', 'Delivery Added!', 'You have successfully added a new delivery.')
    yarn.value = await getYarnById(yarnId)
    deliveries.value = yarn.value?.mergeDeliveries || []
    emit('yarnUpdated', yarn.value)
  })
  .catch((error) => {
    deliveries.value = deliveries.value.filter((item) => item.id != id)
    showToast('error', 'Delivery Not Added!', error.response.data.message)
  })
  .finally(() => {
    loading.value = false
  })
}

const handleUpdate = async (index: number, id: any) => {
  const item = deliveries.value.find((item) => item.id == id)

  const updatedDeliveryAmount = Number(item?.kiloOfYarnDelivered)

  const existingDeliveredAmount = deliveries.value
    .filter(delivery => delivery.id !== id)
    .reduce((total, delivery) => total + Number(delivery.kiloOfYarnDelivered), 0)

  const totalDeliveredAfterUpdate = existingDeliveredAmount + updatedDeliveryAmount

  if (yarn.value?.totalKgOrdered && totalDeliveredAfterUpdate > yarn.value.totalKgOrdered) {
    const message = `Warning: This update would result in a total delivered amount of ${totalDeliveredAfterUpdate} kg, which exceeds the total ordered amount of ${yarn.value.totalKgOrdered} kg. Do you want to continue?`

    showWarningModal.value = true
    warningMessage.value = message
    pendingOperation.value = {
      type: 'update',
      id,
      callback: () => performUpdate(id, item)
    }
    return
  }

  await performUpdate(id, item)
}

const performUpdate = async (id: string, item: any) => {
  const delivery = {
    kiloOfYarnDelivered: Number(item?.kiloOfYarnDelivered),
    dateReceived: item?.dateReceived,
    supplierId: yarn.value?.supplierId
  }

  await updateMergeDelivery(yarnId, id, delivery)
  .then(async () => {
    showToast('success', 'Delivery Updated!', 'You have successfully updated the delivery.')
    yarn.value = await getYarnById(yarnId)
    deliveries.value = yarn.value?.mergeDeliveries || []
    emit('yarnUpdated', yarn.value)
  })
  .catch(async (error) => {
    showToast('error', 'Delivery Not Updated!', error.response.data.message)
    yarn.value = await getYarnById(yarnId)
    deliveries.value = yarn.value?.mergeDeliveries || []
  })
  .finally(() => {
    loading.value = false
    editingRow.value = null
  })
}

const handleWarningConfirm = () => {
  if (pendingOperation.value) {
    pendingOperation.value.callback()
  }
  showWarningModal.value = false
  pendingOperation.value = null
}

const handleWarningCancel = () => {
  if (pendingOperation.value?.type === 'add') {
    deliveries.value = deliveries.value.filter((item) => item.id !== pendingOperation.value?.id)
  } else if (pendingOperation.value?.type === 'update') {
    refreshData()
  }
  showWarningModal.value = false
  pendingOperation.value = null
}

const refreshData = async () => {
  yarn.value = await getYarnById(yarnId)
  deliveries.value = yarn.value?.mergeDeliveries || []
  editingRow.value = null
}

const deleteDelivery = async (deliveryId: string) => {
  loading.value = true

  await deleteMergeDelivery(yarnId, deliveryId)
    .then(async () => {
      showToast('success', 'Delivery Deleted!', 'You have successfully deleted the delivery.')
      yarn.value = await getYarnById(yarnId)
      deliveries.value = yarn.value?.mergeDeliveries || []
      emit('yarnUpdated', yarn.value)
    })
    .catch((error) => {
      showToast('error', 'Delivery Not Deleted!', error.response?.data?.message || 'Failed to delete delivery')
    })
    .finally(() => {
      loading.value = false
    })
}

const parseDateForCalendar = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null
  try {
    return new Date(dateString)
  } catch {
    return null
  }
}

const formatDateForBackend = (date: Date | null): string | null => {
  if (!date) return null
  return date.toISOString()
}

onMounted(async () => {
  loading.value = true
  yarn.value = await getYarnById(yarnId);
  deliveries.value = yarn.value?.mergeDeliveries || []

  if (yarn.value?.supplierId) {
    try {
      const supplier = await getSupplierById(yarn.value.supplierId)
      supplierName.value = supplier.name
    } catch (error) {
      supplierName.value = 'Unknown Supplier'
    }
  }

  loading.value = false
})
</script>

<template>
  <DataTable
    :value="filteredLinkedProducts"
    paginator
    :rows="10"
    tableStyle="min-width: 50rem"
    stripedRows
    paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
    :rowsPerPageOptions="[10, 25]"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} deliveries"
    :loading="loading"
    :pt="{
      table: loading && 'min-h-[30rem]'
    }"
    :pt-options="{
      mergeProps: true
    }"
  >
    <template #empty> <p class="text-center">No delivery found.</p> </template>
    <template #header>
      <div class="flex items-center justify-between font-normal">
        <TableSearchInput v-model:searchQuery="searchQuery" />
        <TableFilter />
      </div>
    </template>

    <Column field="kiloOfYarnDelivered" header="Kilo of Yarn Delivered" sortable>
      <template #body="slotProps">
        <p v-if="!isEditingRow(slotProps.data.id)" class="px-4">
          {{ slotProps.data.kiloOfYarnDelivered }} Kg
        </p>
        <InputText
          v-else
          v-model="slotProps.data.kiloOfYarnDelivered"
          :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
          placeholder="Enter kilo delivered"
        />
      </template>
    </Column>

    <Column field="dateReceived" header="Date Received" sortable>
      <template #body="slotProps">
        <p v-if="!isEditingRow(slotProps.data.id)" class="px-4">
          {{ formatedDate(slotProps.data.dateReceived) }}
        </p>
        <Calendar
          v-else
          :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
          placeholder="Enter date received"
          dateFormat="dd/mm/yy"
          showIcon
          :showButtonBar="true"
          :modelValue="parseDateForCalendar(slotProps.data.dateReceived)"
          @update:modelValue="(date) => slotProps.data.dateReceived = formatDateForBackend(date as Date)"
        />
      </template>
    </Column>

    <Column field="supplierId" header="Supplier" sortable>
      <template #body="slotProps">
        <RouterLink
          :to="`/suppliers/${yarn?.supplierId}`"
          class="font-medium text-neutral-800 block hover:underline"
        >
          {{ getSupplierName(yarn?.supplierId) }}
        </RouterLink>
      </template>
    </Column>

    <Column field="" header="">
      <template #body="slotProps">
        <div class="flex gap-1">
          <Button
            v-ripple
            outlined
            size="small"
            class="flex-1 gap-x-2"
            @click="handleEnableEdit(slotProps.data.id)"
            :loading="loading"
            :disabled="false"
            v-if="!isEditingRow(slotProps.data.id) && !slotProps.data.isNew"
          >
            <span class="text-xs">Edit</span>
            <Icon icon="basil:edit-outline" class="text-lg" />
          </Button>
          <Button
            v-ripple
            outlined
            severity="danger"
            size="small"
            class="flex-1 gap-x-2"
            @click="deleteDelivery(slotProps.data.id)"
            :loading="loading"
            :disabled="false"
            v-if="!isEditingRow(slotProps.data.id) && !slotProps.data.isNew"
          >
            <span class="text-xs">Delete</span>
            <Icon icon="basil:trash-outline" class="text-lg" />
          </Button>
          <div v-else class="flex gap-1">
            <Button
              v-ripple
              outlined
              size="small"
              class="flex-1 gap-x-2"
              @click="handleSave(slotProps.index, slotProps.data.id)"
              :loading="loading"
              :disabled="false"
              v-if="slotProps.data.isNew"
            >
              <span class="text-xs">Save</span>
              <Icon icon="basil:check-outline" class="text-xl" />
            </Button>
            <Button
              v-ripple
              outlined
              size="small"
              class="flex-1 gap-x-2"
              @click="handleUpdate(slotProps.index, slotProps.data.id)"
              :loading="loading"
              :disabled="false"
              v-else
            >
              <span class="text-xs">Update</span>
              <Icon icon="basil:refresh-outline" class="text-xl" />
            </Button>
          </div>
        </div>
      </template>
    </Column>

    <!------------------------------------------ pagination---------------------------------------------------->
    <template #paginatorprevpagelinkicon>
      <Button size="small" class="space-x-1 items-center" outlined severity="secondary">
        <Icon icon="material-symbols:chevron-left" class="text-2xl" />
        <span>Previous</span>
      </Button>
    </template>
    <template #paginatornextpagelinkicon>
      <Button size="small" outlined severity="secondary" class="space-x-1 items-center">
        <span>Next</span>
        <Icon icon="material-symbols:chevron-right" class="text-2xl" />
      </Button>
    </template>
  </DataTable>
  <div class="px-4 py-2">
    <Button
      v-ripple
      outlined
      class="gap-x-2"
      @click="addDelivery"
      :loading="loading"
      :disabled="isAddingNew"
    >
      <span> Add to this Section </span>
      <Icon icon="basil:plus-solid" class="text-2xl" />
    </Button>
  </div>

  <!-- Warning Modal -->
  <Dialog
    v-model:visible="showWarningModal"
    modal
    :draggable="false"
    :style="{ width: 'min(95vw, 35rem)' }"
    :breakpoints="{ '640px': '90vw' }"
    :closable="false"
    :dismissableMask="true"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <Icon icon="material-symbols:warning" class="text-2xl text-yellow-600" />
        <h3 class="text-lg font-semibold">Warning</h3>
      </div>
    </template>

    <div class="p-4">
      <p class="text-gray-700 leading-relaxed">{{ warningMessage }}</p>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3">
        <Button
          outlined
          @click="handleWarningCancel"
          label="Cancel"
          class="min-w-32"
        >
          <template #icon>
            <Icon icon="mdi:close" class="text-xl ml-3 order-1" />
          </template>
        </Button>
        <Button
          @click="handleWarningConfirm"
          label="Continue"
          severity="warning"
          class="min-w-32"
        >
          <template #icon>
            <Icon icon="mdi:check" class="text-xl ml-3 order-1" />
          </template>
        </Button>
      </div>
    </template>
  </Dialog>
</template>
